import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Typography,
} from "@mui/material";
import axios from 'axios';
import { toast, ToastContainer, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Edit({ handleCloseModal, openModal, formData, setFormData, editExpense, fetchExpenses, activeFilter, page }) {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [isSubCategoryLoading, setIsSubCategoryLoading] = useState(false);

  // Fetch categories on mount
  const fetchCategory = async () => {
    try {
      setIsCategoryLoading(true);
      const token = localStorage.getItem("tokenexpense");
      const response = await axios.get(
        `https://expense.dpstrust.in/api/category/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCategories(response.data.data.categoryData);
    } catch (error) {
      console.error("Error fetching category:", error);
    } finally {
      setIsCategoryLoading(false);
    }
  };

  // Fetch subcategories based on selected category
  const fetchSubCategory = async (categoryId) => {
    try {
      setIsSubCategoryLoading(true);
      const token = localStorage.getItem("tokenexpense");
      const response = await axios.get(
        `https://expense.dpstrust.in/api/subcategory/all?categoryId=${categoryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSubCategories(response.data.data.subcategories || []);
    } catch (error) {
      console.error("Error fetching subcategory:", error);
    } finally {
      setIsSubCategoryLoading(false);
    }
  };

  useEffect(() => {
    // Fetch categories once when component mounts
    fetchCategory();
  }, []); // Empty dependency array to run only once
  
  useEffect(() => {
    // If formData.category changes, fetch the corresponding subcategories
    if (formData.category) {
      const selectedCategory = categories.find(
        (cat) => cat.name === formData.category
      );
      if (selectedCategory) {
        fetchSubCategory(selectedCategory._id);
      }
    }
  }, [formData.category, categories]); // Run when formData.category or categories changes
  

  const handleFormChange = async (e) => {
    const { name, value } = e.target;

    if (name === "category") {
      // Find the selected category object based on its name
      const selectedCategory = categories.find((cat) => cat.name === value);

      // Update the form data with the selected category name
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        subCategory: "", // Reset sub-category when category changes
      }));

      // If the category is valid, fetch sub-categories
      if (selectedCategory?._id) {
        await fetchSubCategory(selectedCategory._id);
      } else {
        setSubCategories([]); // Reset sub-categories if no valid category is selected
      }
    } else {
      // For other fields, just update the form data
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSaveExpense = async () => {
    if (!formData.description || !formData.category || !formData.subCategory || !formData.date || !formData.amount) {
      toast.info("Please fill all the required fields.", {
        position: "top-right",
        autoClose: 5000,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }
    const token = localStorage.getItem("tokenexpense");
    try {
      const response = await axios.put(
        `https://expense.dpstrust.in/api/expense/${editExpense._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchExpenses(token, page, activeFilter);
      handleCloseModal();
    } catch (error) {
      console.error("Error saving expense:", error);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "white",
    padding: 3,
    borderRadius: 2,
    boxShadow: 24,
    width: "90%"
  };

  const buttonContainerStyle = {
    marginTop: 20,
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  };

  const buttonStyle = {
    flex: 1,
    maxWidth: "150px",
  };

  return (<>
  <ToastContainer/>
    <Modal open={openModal} onClose={handleCloseModal}>
      <Box sx={modalStyle}>
        <Typography variant="h6" gutterBottom>
          Edit Expense
        </Typography>
        <TextField
          label="Description"
          variant="outlined"
          fullWidth
          name="description"
          value={formData.description}
          onChange={handleFormChange}
          margin="normal"
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            name="category"
            value={formData.category}
            onChange={handleFormChange}
          >
            {categories.map((category) => (
              <MenuItem key={category._id} value={category.name}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Sub-category</InputLabel>
          <Select
            label="Sub-category"
            name="subCategory"
            value={formData.subCategory}
            onChange={handleFormChange}
          >
            {subCategories.map((subCategory) => (
              <MenuItem key={subCategory._id} value={subCategory.name}>
                {subCategory.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Date"
          variant="outlined"
          fullWidth
          type="date"
          name="date"
          value={formData.date}
          onChange={handleFormChange}
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          label="Amount"
          variant="outlined"
          fullWidth
          name="amount"
          value={formData.amount}
          onChange={handleFormChange}
          margin="normal"
          type="number"
        />
        <div style={buttonContainerStyle}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveExpense}
            style={buttonStyle}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseModal}
            style={buttonStyle}
          >
            Cancel
          </Button>
        </div>
      </Box>
    </Modal>
    </>
  );
}

export default Edit;
